import {
  getNewStoryboardPosition,
  getUnexpiredToken,
  useActiveCanvas,
  useActiveProject,
  useCanvasId,
} from "@/utils/utils";
import { STORYBOARD_TYPES } from "@/utils/constants";
import { v4 as uuidv4 } from "uuid";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { useSupabase } from "@/components/contexts/DatabaseSyncWrapper";
import { updateCanvasStoryboards } from "@/store/supabaseSlice";
import { ProjectRunMode } from "@/utils/templateProjects";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { Storyboard } from "@/utils/types";
import { useSession } from "@clerk/clerk-react";
import useActivityStore from "@/store/zustand/activityStore";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";

export const useDuplicateStoryboard = () => {
  const canvasId = useCanvasId();

  const project = useActiveProject();

  const dispatch = useDispatch<any>();

  const treeElementLookup = useCodebaseStore(
    (state) => state.treeElementLookup
  );
  const updateLookupsBasedOnPartialResponse = useCodebaseStore(
    (state) => state.updateLookupsBasedOnPartialResponse
  );

  const canvas = useActiveCanvas();

  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);

  const triggerRefetchActivityStream = useActivityStore(
    (state) => state.triggerRefetchActivityStream
  );

  const { session } = useSession();

  const duplicateStoryboard = async (
    storyboard: Storyboard,
    origin?: {
      x: number;
      y: number;
    }
  ) => {
    if (!supabaseClient) {
      console.error("Supabase client not available");
      return;
    }

    const supabaseJWT = await getUnexpiredToken(session);

    const width = storyboard.width;
    const height = storyboard.height;

    const insertX = origin?.x || storyboard.x + width + 20;
    const insertY = origin?.y || storyboard.y;

    const maxZIndex = canvas?.storyboards
      ?.map((storyboard: any) => storyboard.zIndex)
      ?.reduce((a: any, b: any) => Math.max(a, b), 0);

    let newStoryboard;
    // Duplicate the contents of the storyboard
    if (storyboard.type !== STORYBOARD_TYPES.APPLICATION && !storyboard.saved) {
      // Get all names for the new storyboard name
      const storyboardAndComponentNames = new Set();

      // @ts-expect-error TS18047
      canvas.storyboards
        .filter(
          (storyboard: any) => storyboard.type !== STORYBOARD_TYPES.APPLICATION
        )
        .filter(
          (storyboard: any) =>
            storyboard.deleted !== true && !storyboard.preCreated
        )
        .forEach((storyboard: any) => {
          storyboardAndComponentNames.add(storyboard.name);
        });

      Object.values(treeElementLookup).forEach((treeElement: any) => {
        if (treeElement.type === "component") {
          storyboardAndComponentNames.add(treeElement.name);
        }
      });

      let storyboardIndex = 1;
      while (storyboardAndComponentNames.has(`Storyboard${storyboardIndex}`)) {
        storyboardIndex++;
      }

      const storyboardName = `Storyboard${storyboardIndex}`;
      const storyboardId = uuidv4().toString();

      const res = await axios.post(
        `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/canvases/${canvasId}/parseAndMutate/mutate/duplicateUnsavedComponent`,
        {
          componentName: storyboardName,
          sourceStoryboardId: storyboard.id,
          newStoryboardId: storyboardId,
        },
        {
          headers: {
            Authorization: `Bearer ${supabaseJWT}`,
          },
        }
      );

      const treeElements = res.data?.treeElementLookup;
      const createdComponent: any = Object.values(treeElements).find(
        (treeElement: any) => treeElement.type === "component"
      );

      let fullFilePath = createdComponent?.filename;

      updateLookupsBasedOnPartialResponse(res.data);
      triggerRefetchActivityStream();

      newStoryboard = {
        name: storyboardName,
        id: storyboardId,
        type: STORYBOARD_TYPES.COMPONENT,
        width,
        height,
        x: insertX,
        y: insertY,
        zIndex: maxZIndex + 10,
        created: true,
        fullFilePath,
      };
    } else {
      // Just duplicate the storyboard without modifying code
      newStoryboard = {
        ...storyboard,
        id: uuidv4().toString(),
        width,
        height,
        x: insertX,
        y: insertY,
        zIndex: maxZIndex + 10,
      };
    }

    // @ts-expect-error TS18047
    const newStoryboards = [...canvas.storyboards, newStoryboard];

    await updateCanvasStoryboards(
      supabaseClient,
      dispatch,
      newStoryboards,
      // @ts-expect-error TS2345
      canvas
    );
  };

  return duplicateStoryboard;
};
